// =============================================================================
// MeuDriver — Batch O4: useAdminPayouts
// Hooks para o painel de gestão de payouts do admin.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface AdminPayoutRow {
  payout_id: string
  instructor_id: string
  instructor_name: string | null
  instructor_email: string | null
  amount: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  requested_at: string
  processed_at: string | null
  mp_user_id: string | null
  mp_email: string | null
  mp_verified: boolean
}

export interface FinancialSummary {
  total_gmv: number
  total_commission: number
  total_paid_out: number
  pending_payouts: number
  platform_balance: number
}

export type PayoutStatusFilter = 'pending' | 'processing' | 'completed' | 'failed' | undefined

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const adminPayoutKeys = {
  all: ['admin-payouts'] as const,
  list: (status?: string) => [...adminPayoutKeys.all, 'list', status ?? 'all'] as const,
  summary: () => [...adminPayoutKeys.all, 'summary'] as const,
}

// ---------------------------------------------------------------------------
// useAdminPayoutRequests — lista payouts via RPC admin_list_payout_requests
// ---------------------------------------------------------------------------

export function useAdminPayoutRequests(status?: PayoutStatusFilter) {
  return useQuery<AdminPayoutRow[]>({
    queryKey: adminPayoutKeys.list(status),
    staleTime: 1000 * 60,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_list_payout_requests', {
        p_status: status ?? null,
      })
      if (error) throw new Error(error.message)
      return (data ?? []) as AdminPayoutRow[]
    },
  })
}

// ---------------------------------------------------------------------------
// useAdminApproveMpAccount — aprova conta MP de um instrutor
// ---------------------------------------------------------------------------

export function useAdminApproveMpAccount() {
  const qc = useQueryClient()

  return useMutation<void, Error, { instructorId: string }>({
    mutationFn: async ({ instructorId }) => {
      const { error } = await supabase.rpc('admin_approve_mp_account', {
        p_instructor_id: instructorId,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminPayoutKeys.all })
      toast.success('Conta Mercado Pago aprovada com sucesso.')
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })
}

// ---------------------------------------------------------------------------
// useAdminFinancialSummary — KPIs financeiros via RPC admin_get_financial_summary
// ---------------------------------------------------------------------------

export function useAdminFinancialSummary() {
  return useQuery<FinancialSummary>({
    queryKey: adminPayoutKeys.summary(),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_financial_summary')
      if (error) throw new Error(error.message)

      // RPC retorna array de uma linha
      const row = Array.isArray(data) ? data[0] : data
      return {
        total_gmv: Number(row?.total_gmv ?? 0),
        total_commission: Number(row?.total_commission ?? 0),
        total_paid_out: Number(row?.total_paid_out ?? 0),
        pending_payouts: Number(row?.pending_payouts ?? 0),
        platform_balance: Number(row?.platform_balance ?? 0),
      }
    },
  })
}
